import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { useFormik } from 'formik'
import { useSnackbar } from 'notistack'
import * as yup from 'yup'
import { map } from 'lodash'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { useNodes } from '../../../../contexts/NodesContext'
import useTeamPackageImportsResources from '../../../../shared/resources/api/v1/admin/packages/useTeamPackageImportsResources'
import FormikTextField from '../../../../shared/components/formik/FormikTextField'
import FormikSelectField from '../../../../shared/components/formik/FormikSelectField'
import FlexColumn from '../../../../shared/components/layouts/FlexColumn'

const PackageImportCreateDialog = ({ open, onClose, pkg }) => {
  const { teamSlug } = useParams()
  const navigate = useNavigate()
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const { nodes } = useNodes()
  const { create } = useTeamPackageImportsResources(teamSlug)

  const [submitting, setSubmitting] = useState(false)

  const nodeOptions = useMemo(() => {
    return map(nodes, (node) => ({
      value: node.id,
      label: node.name
    }))
  }, [nodes])

  const validationSchema = useMemo(() => {
    return yup.object({
      projectName: yup.string().required(formatMessage({ id: 'validation.required' })),
      nodeId: yup.string().required(formatMessage({ id: 'validation.required' }))
    })
  }, [formatMessage])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      projectName: pkg?.name || '',
      nodeId: ''
    },
    validationSchema,
    onSubmit: (values) => {
      setSubmitting(true)
      const data = {
        packageImport: {
          packageId: pkg.id,
          projectName: values.projectName,
          nodeId: values.nodeId
        }
      }

      create(data)
        .then((response) => {
          const packageImport = response.packageImport
          enqueueSnackbar(formatMessage({ id: 'packageImports.createDialog.success' }), { variant: 'success' })
          onClose()
          navigate(`/teams/${teamSlug}/docker/package-imports/${packageImport.id}`)
        })
        .catch((err) => {
          enqueueSnackbar(err.message || formatMessage({ id: 'packageImports.createDialog.error' }), { variant: 'error' })
        })
        .finally(() => {
          setSubmitting(false)
        })
    }
  })

  const handleClose = () => {
    if (!submitting) {
      formik.resetForm()
      onClose()
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <FormattedMessage id="packageImports.createDialog.title" />
        {pkg && `: ${pkg.name}`}
      </DialogTitle>
      <DialogContent>
        <FlexColumn gap={2} sx={{ mt: 1 }}>
          <FormikTextField
            formik={formik}
            name="projectName"
            label={<FormattedMessage id="packageImports.createDialog.projectNameLabel" />}
            helperText={formatMessage({ id: 'packageImports.createDialog.projectNameHelperText' })}
          />
          <FormikSelectField
            formik={formik}
            name="nodeId"
            label={<FormattedMessage id="packageImports.createDialog.nodeLabel" />}
            options={nodeOptions}
            helperText={formatMessage({ id: 'packageImports.createDialog.nodeHelperText' })}
          />
        </FlexColumn>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose} disabled={submitting}>
          <FormattedMessage id="app.actions.cancel.label" />
        </Button>
        <Button
          variant="contained"
          onClick={formik.handleSubmit}
          disabled={submitting || !formik.isValid}
        >
          {submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <FormattedMessage id="packageImports.createDialog.submitButton" />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PackageImportCreateDialog
